// ArchitectureTemplate — infographic.
// Left-to-right node-and-edge data flow diagram.
// Stages contain nodes; edges connect adjacent stages with hairline arrows.
// Reference: Stripe payments architecture diagrams + CBIC RAG SVG on
// the static site (work-cbic-rag.html).

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Eyebrow } from '../stage/Eyebrow';
import { PointableItem, DefaultOrbAnchor } from '../stage/PointableItem';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface ArchitectureProps {
  content: SceneContent;
}

export function ArchitectureTemplate({ content }: ArchitectureProps) {
  const { headline, eyebrow, stages = [] } = content;
  const colsClass =
    stages.length === 2 ? 'grid-cols-2' :
    stages.length === 3 ? 'grid-cols-3' :
    'grid-cols-4';

  return (
    <Stage>
      {/* Orb anchor — top-right, 48px. Hidden when pointing at a node. */}
      <div className="absolute top-12 right-12 max-md:top-6 max-md:right-6">
        <DefaultOrbAnchor size={48} />
      </div>

      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.12)}
        className="m-auto w-full max-w-[1440px] flex flex-col gap-16"
      >
        {(eyebrow || headline) && (
          <div className="text-center flex flex-col gap-6 items-center">
            {eyebrow && (
              <motion.div variants={fadeUp}>
                <Eyebrow>{eyebrow}</Eyebrow>
              </motion.div>
            )}
            {headline && (
              <motion.h2
                variants={fadeUp}
                className="text-headline font-display max-w-[32ch] text-balance text-fg"
              >
                {headline}
              </motion.h2>
            )}
          </div>
        )}

        <motion.div
          variants={stagger(0.15)}
          className={`grid ${colsClass} gap-12 max-md:grid-cols-1 max-md:gap-10 relative`}
        >
          {stages.map((stage, si) => (
            <motion.div
              key={si}
              variants={fadeUp}
              className="relative flex flex-col gap-6"
            >
              {/* Stage label */}
              <span className="text-eyebrow text-fg-mute uppercase font-medium tracking-[0.18em]">
                {`PHASE ${String(si + 1).padStart(2, '0')} — ${stage.label}`}
              </span>

              {/* Edge to next stage (desktop only) */}
              {si < stages.length - 1 && (
                <span
                  aria-hidden
                  className="hidden md:block absolute top-[14px] left-[calc(100%+0.5rem)] w-[calc(3rem-1rem)] h-px bg-gradient-to-r from-fg-faint to-fg-faint/0"
                />
              )}
              {/* Vertical arrow for mobile flow */}
              {si < stages.length - 1 && (
                <span
                  aria-hidden
                  className="md:hidden mx-auto w-px h-8 bg-gradient-to-b from-fg-faint to-fg-faint/0"
                />
              )}

              {/* Nodes inside the stage. Each node is pointable by its
                  schema-supplied id (stages[s].nodes[n].id). */}
              <motion.div variants={stagger(0.08)} className="flex flex-col gap-3">
                {stage.nodes.map((n, ni) => (
                  <motion.div key={ni} variants={fadeUp}>
                    <PointableItem
                      orbTargetId={n.id}
                      orbSize={36}
                      orbPlacement="top-right"
                      className="rounded-xl border border-fg-faint/50 bg-void-800/80 px-5 py-4 flex flex-col gap-1 backdrop-blur-sm"
                    >
                      <span className="text-body font-display text-fg font-medium">{n.label}</span>
                      {n.sublabel && (
                        <span className="text-caption font-mono text-fg-mute">{n.sublabel}</span>
                      )}
                    </PointableItem>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </Stage>
  );
}
